export interface LinePoint {
  key: string;
  /** Shown under the first and last points. */
  label: string;
  value: number;
}

interface LineChartProps {
  points: LinePoint[];
  ariaLabel: string;
  /** Text for the highest and lowest values on the left. */
  formatValue(value: number): string;
  color?: string;
}

const W = 320;
const H = 120;
const PAD = 10;

/**
 * A small line over time (Shopping price history). Uniformly scaled SVG so the dots stay round;
 * the points are listed in text on the same page, so the chart itself is one labelled image.
 */
export function LineChart({
  points,
  ariaLabel,
  formatValue,
  color = 'var(--chart-1)',
}: LineChartProps) {
  if (points.length === 0) return null;
  const values = points.map((p) => p.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const x = (i: number) =>
    points.length === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (points.length - 1);
  const y = (v: number) => (max === min ? H / 2 : PAD + ((max - v) * (H - 2 * PAD)) / span);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join(' ');
  const first = points[0]!;
  const last = points[points.length - 1]!;

  return (
    <figure role="img" aria-label={ariaLabel} className="flex flex-col gap-1">
      <div className="flex gap-2">
        <div
          aria-hidden="true"
          className="flex w-14 shrink-0 flex-col justify-between py-1 text-right font-mono text-[11px] tabular-nums text-muted"
        >
          <span>{formatValue(max)}</span>
          {max !== min ? <span>{formatValue(min)}</span> : null}
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="min-w-0 flex-1"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <line x1={0} x2={W} y1={H - 1} y2={H - 1} stroke="var(--divider)" strokeWidth={1} />
          <path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {points.map((p, i) => (
            <circle
              key={p.key}
              cx={x(i)}
              cy={y(p.value)}
              r={i === points.length - 1 ? 5 : 3.5}
              fill={i === points.length - 1 ? 'var(--accent)' : color}
            />
          ))}
        </svg>
      </div>
      <div aria-hidden="true" className="ml-16 flex justify-between text-[11px] text-muted">
        <span>{first.label}</span>
        {points.length > 1 ? <span>{last.label}</span> : null}
      </div>
    </figure>
  );
}
